/**
 * Asset Breakdown Panel
 *
 * Shows emissions distribution by asset type, with optional scope filtering.
 * Two visual modes:
 *   - "cards" (default): horizontal bars + top emitters table (Image 1 style)
 *   - "donut": pie chart + progress bars (Image 2 / CategoryBreakdownPanel style)
 */

import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowRight, Building2, Loader2 } from 'lucide-react';
import { PieChart, Pie, Cell, Label } from 'recharts';
import { Card, CardContent, CardTitle, CardHeader } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@g17eco/ui-modern/components/ui/chart';
import { Progress } from '@g17eco/ui-modern/components/ui/progress';
import { useGetAssetsQuery, useGetAssetComparisonQuery, useGetEmissionsSummaryQuery } from '../api/emissions';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { getAssetTypeBrand } from '../config/assetTypeBranding';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrl } from '@routes/util';
import type { AssetComparisonItem, EmissionScope } from '../types/emissions';

/** Hex colours for asset types in the donut chart (keyed by AssetType enum values). */
const ASSET_TYPE_COLORS: Record<string, string> = {
  facility: '#2563eb',
  vehicle: '#ea580c',
  manufacturing_facility: '#475569',
  data_centre: '#9333ea',
  equipment: '#d97706',
  other: '#6b7280',
};

export const getAssetTypeColor = (type: string): string =>
  ASSET_TYPE_COLORS[type] ?? '#6b7280';

const ViewAllLink = ({ initiativeId }: { initiativeId: string }) => {
  const history = useHistory();
  const url = generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSETS, { rootAppPath, initiativeId });
  return (
    <Button
      variant='link'
      size='sm'
      onClick={() => history.push(url)}
    >
      View all
      <ArrowRight />
    </Button>
  );
};

// ─── Reusable details list (progress bars with coloured icons) ──────────────

interface AssetTypeDetailsListProps {
  typeDistribution: Array<{ type: string; co2e: number }>;
  totalCo2e: number;
  formatCompact: (kg: number) => { formatted: string; unit: string; display: string };
}

export const AssetTypeDetailsList = ({ typeDistribution, totalCo2e, formatCompact }: AssetTypeDetailsListProps) => (
  <>
    {typeDistribution.map(({ type, co2e }) => {
      const brand = getAssetTypeBrand(type);
      const percentage = totalCo2e > 0 ? (co2e / totalCo2e) * 100 : 0;
      const color = getAssetTypeColor(type);
      return (
        <div key={type} className='tw:py-2'>
          <div className='tw:flex tw:justify-between tw:items-center tw:mb-2'>
            <div className='tw:flex tw:items-center tw:gap-2'>
              <div
                className='tw:rounded tw:flex tw:items-center tw:justify-center tw:shrink-0'
                style={{ width: 22, height: 22, backgroundColor: `${color}22` }}
              >
                <brand.Icon className='tw:w-3 tw:h-3' style={{ color }} />
              </div>
              <span className='tw:font-medium tw:text-sm'>{brand.label}</span>
            </div>
            <div className='tw:text-right'>
              <span className='tw:font-bold tw:text-sm tw:mr-2'>{formatCompact(co2e).display}</span>
              <span className='text-ThemeTextLight tw:text-sm'>{percentage.toFixed(0)}%</span>
            </div>
          </div>
          <Progress
            value={percentage}
            className='tw:h-2'
            progressColor={color}
          />
        </div>
      );
    })}
  </>
);

interface AssetBreakdownPanelProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  includeChildren?: boolean;
  /** Scope filter — when set, only emissions from the given scope are counted. */
  scope?: EmissionScope;
  /** Visual variant: "cards" = bars + table, "donut" = pie chart + progress bars. */
  variant?: 'cards' | 'donut';
  onAssetSelect?: (assetId: string) => void;
}

/** Sum CO₂e for a single asset, optionally filtered by scope. */
const assetCo2eForScope = (asset: AssetComparisonItem, scope?: EmissionScope): number => {
  if (!scope) return asset.totalCo2e;
  return asset.scopes.find((s) => s.scope === scope)?.co2e ?? 0;
};

// ─── Cards variant (Image 1) ────────────────────────────────────────────────

interface CardsViewProps {
  initiativeId: string;
  typeDistribution: Array<{ type: string; co2e: number }>;
  topEmitters: Array<AssetComparisonItem & { filteredCo2e: number }>;
  assetTotalCo2e: number;
  orgTotalCo2e: number;
  formatCompact: (kg: number) => { formatted: string; unit: string; display: string };
  onAssetSelect?: (assetId: string) => void;
}

const CardsView = ({ initiativeId, typeDistribution, topEmitters, assetTotalCo2e, orgTotalCo2e, formatCompact, onAssetSelect }: CardsViewProps) => (
  <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6'>
    {/* Asset Type Distribution */}
    <Card>
      <CardHeader>
        <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
          <CardTitle>Emissions by Asset Type</CardTitle>
          <ViewAllLink initiativeId={initiativeId} />
        </div>
      </CardHeader>
      <CardContent>
        {typeDistribution.length === 0 ? (
          <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-8'>No asset data available.</p>
        ) : (
          <AssetTypeDetailsList typeDistribution={typeDistribution} totalCo2e={assetTotalCo2e} formatCompact={formatCompact} />
        )}
      </CardContent>
    </Card>

    {/* Top Emitters */}
    <Card>
      <CardHeader>
        <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
          <CardTitle>Top Emitting Assets</CardTitle>
          <ViewAllLink initiativeId={initiativeId} />
        </div>
      </CardHeader>
      <CardContent>
        {topEmitters.length === 0 ? (
          <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-8'>No asset data available.</p>
        ) : (
          <table className='tw:w-full tw:text-sm'>
            <thead>
              <tr className='tw:text-xs tw:text-muted-foreground'>
                <th className='tw:text-left tw:pb-2 tw:font-medium tw:w-5'>#</th>
                <th className='tw:text-left tw:pb-2 tw:font-medium'>Name</th>
                <th className='tw:text-left tw:pb-2 tw:font-medium tw:w-24'>Type</th>
                <th className='tw:text-right tw:pb-2 tw:font-medium tw:w-12'>%</th>
                <th className='tw:text-right tw:pb-2 tw:font-medium tw:w-20'>CO&#x2082;e</th>
              </tr>
            </thead>
            <tbody>
              {topEmitters.map((asset, i) => {
                const brand = getAssetTypeBrand(asset.assetType);
                const pct = orgTotalCo2e > 0 ? (asset.filteredCo2e / orgTotalCo2e) * 100 : 0;
                return (
                  <tr
                    key={asset._id}
                    className='tw:cursor-pointer tw:hover:bg-muted/50 tw:border-t tw:border-border'
                    onClick={() => onAssetSelect?.(asset._id)}
                  >
                    <td className='tw:py-1.5 tw:text-muted-foreground'>{i + 1}.</td>
                    <td className='tw:py-1.5 tw:truncate tw:max-w-[200px]'>{asset.name}</td>
                    <td className='tw:py-1.5'>
                      <Badge variant='outline' className={`tw:text-xs ${brand.iconColor}`}>{brand.label}</Badge>
                    </td>
                    <td className='tw:py-1.5 tw:text-right tw:text-xs tw:text-muted-foreground'>{pct.toFixed(1)}%</td>
                    <td className='tw:py-1.5 tw:text-right tw:text-xs'>{formatCompact(asset.filteredCo2e).display}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  </div>
);

// ─── Donut variant (Image 2) ────────────────────────────────────────────────

interface DonutViewProps {
  initiativeId: string;
  typeDistribution: Array<{ type: string; co2e: number }>;
  assetTotalCo2e: number;
  formatCompact: (kg: number) => { formatted: string; unit: string; display: string };
}

const DonutView = ({ initiativeId, typeDistribution, assetTotalCo2e, formatCompact }: DonutViewProps) => {
  const chartItems = typeDistribution.map(({ type, co2e }) => ({
    name: getAssetTypeBrand(type).label,
    emissions: co2e,
    fill: getAssetTypeColor(type),
  }));

  const totalFormatted = formatCompact(assetTotalCo2e);

  return (
    <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6 tw:h-full'>
      {/* Donut chart */}
      <Card className='tw:h-full'>
        <CardHeader>
          <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
            <CardTitle>Emissions by Asset Type</CardTitle>
            <ViewAllLink initiativeId={initiativeId} />
          </div>
        </CardHeader>
        <CardContent>
          <ChartContainer config={{}} className='tw:w-full' style={{ height: 200 }}>
            <PieChart>
              <ChartTooltip content={<ChartTooltipContent hideLabel />} />
              <Pie
                data={chartItems}
                dataKey='emissions'
                nameKey='name'
                innerRadius={55}
                outerRadius={80}
                strokeWidth={3}
              >
                {typeDistribution.map(({ type }) => (
                  <Cell key={type} fill={getAssetTypeColor(type)} />
                ))}
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      return (
                        <text x={viewBox.cx} y={viewBox.cy} textAnchor='middle' dominantBaseline='middle'>
                          <tspan x={viewBox.cx} y={viewBox.cy} className='tw:fill-foreground tw:text-xl tw:font-bold'>
                            {totalFormatted.formatted}
                          </tspan>
                          <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 16} className='tw:fill-muted-foreground tw:text-xs'>
                            {totalFormatted.unit}
                          </tspan>
                        </text>
                      );
                    }
                    return null;
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
          <div className='tw:flex tw:flex-wrap tw:justify-center tw:gap-2 tw:mt-0'>
            {typeDistribution.map(({ type }) => (
              <div key={type} className='tw:flex tw:items-center tw:gap-2'>
                <span className='tw:rounded-full tw:shrink-0' style={{ width: 10, height: 10, backgroundColor: getAssetTypeColor(type) }} />
                <span className='tw:text-xs'>{getAssetTypeBrand(type).label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Details with progress bars */}
      <Card className='tw:h-full'>
        <CardHeader>
          <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
            <CardTitle>Asset Type Details</CardTitle>
            <ViewAllLink initiativeId={initiativeId} />
          </div>
        </CardHeader>
        <CardContent>
          <AssetTypeDetailsList typeDistribution={typeDistribution} totalCo2e={assetTotalCo2e} formatCompact={formatCompact} />
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Empty states ───────────────────────────────────────────────────────────

/**
 * Rendered when the initiative has no assets registered at all. Primary nudge
 * is toward the Manage Assets page — where the user can add assets in bulk or
 * one at a time.
 */
const NoAssetsCta = ({ initiativeId }: { initiativeId: string }) => {
  const history = useHistory();
  const goToAssets = () =>
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ASSETS, { rootAppPath, initiativeId }));

  return (
    <Card>
      <CardContent className='tw:flex tw:flex-col tw:items-center tw:text-center tw:py-10 tw:px-6'>
        <div className='tw:mb-3 tw:flex tw:size-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-purple-100 tw:text-purple-600'>
          <Building2 className='tw:size-6' />
        </div>
        <p className='tw:text-sm tw:font-medium tw:mb-1'>No assets registered</p>
        <p className='tw:text-xs tw:text-muted-foreground tw:max-w-md tw:mb-4'>
          Group emissions by facility, fleet, data centre, or other asset types
          to see where your biggest impact is. Register your first asset to see
          this breakdown.
        </p>
        <Button variant='emerald' size='sm' onClick={goToAssets}>
          <Building2 className='tw:size-4' />
          Add Asset
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * Rendered when the initiative has assets but no emissions rolled up to them
 * for the active period/scope filter. The user already knows about assets, so
 * this is purely informational — no CTA.
 */
const NoEmissionsForAssetsMessage = ({ scope }: { scope?: EmissionScope }) => {
  const scopeLabel = scope ? scope.replace('scope', 'Scope ') : 'this period';
  return (
    <Card>
      <CardHeader>
        <CardTitle>Emissions by Asset Type</CardTitle>
      </CardHeader>
      <CardContent className='tw:flex tw:flex-col tw:items-center tw:text-center tw:py-8 tw:px-6 tw:text-muted-foreground'>
        <Building2 className='tw:size-8 tw:mb-2 tw:opacity-40' />
        <p className='tw:text-sm tw:mb-0'>
          No asset-linked emissions for {scopeLabel}. Activities may exist but
          aren&apos;t attributed to an asset yet.
        </p>
      </CardContent>
    </Card>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

export const AssetBreakdownPanel = ({
  initiativeId,
  periodStart,
  periodEnd,
  includeChildren,
  scope,
  variant = 'cards',
  onAssetSelect,
}: AssetBreakdownPanelProps) => {
  const { formatCompact } = useEmissionsFormatter();

  const { data: assetsResponse, isLoading: assetsLoading } = useGetAssetsQuery({ initiativeId, limit: 100, includeChildren });
  const { data: comparisonData, isLoading: comparisonLoading } = useGetAssetComparisonQuery({ initiativeId, periodStart, periodEnd, includeChildren });
  const { data: orgSummary } = useGetEmissionsSummaryQuery({ initiativeId, periodStart, periodEnd, includeChildren });

  const assets = useMemo(() => assetsResponse?.data ?? [], [assetsResponse?.data]);
  const comparisonAssets = useMemo(() => comparisonData?.assets ?? [], [comparisonData?.assets]);
  const orgTotalCo2e = orgSummary?.totalCO2e ?? 0;

  const isLoading = assetsLoading || comparisonLoading;

  // Group by asset type, applying scope filter
  const typeDistribution = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const asset of assets) {
      const comparison = comparisonAssets.find((c) => c._id === asset._id);
      const co2e = comparison ? assetCo2eForScope(comparison, scope) : 0;
      acc[asset.assetType] = (acc[asset.assetType] ?? 0) + co2e;
    }
    return Object.entries(acc)
      .map(([type, co2e]) => ({ type, co2e }))
      .filter(({ co2e }) => co2e > 0)
      .sort((a, b) => b.co2e - a.co2e);
  }, [assets, comparisonAssets, scope]);

  const assetTotalCo2e = typeDistribution.reduce((sum, d) => sum + d.co2e, 0);

  const topEmitters = useMemo(() =>
    [...comparisonAssets]
      .map((asset) => ({ ...asset, filteredCo2e: assetCo2eForScope(asset, scope) }))
      .filter((a) => a.filteredCo2e > 0)
      .sort((a, b) => b.filteredCo2e - a.filteredCo2e)
      .slice(0, 5),
    [comparisonAssets, scope],
  );

  if (isLoading) {
    return (
      <div className='tw:flex tw:items-center tw:justify-center tw:py-10'>
        <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
      </div>
    );
  }

  // Two distinct empty states:
  // - No assets registered at all → onboarding prompt with "Add Asset" CTA
  // - Assets exist but no emissions in this period/scope → data-gap message
  if (assets.length === 0) {
    return <NoAssetsCta initiativeId={initiativeId} />;
  }

  if (typeDistribution.length === 0 && topEmitters.length === 0) {
    return <NoEmissionsForAssetsMessage scope={scope} />;
  }

  if (variant === 'donut') {
    return <DonutView initiativeId={initiativeId} typeDistribution={typeDistribution} assetTotalCo2e={assetTotalCo2e} formatCompact={formatCompact} />;
  }

  return (
    <CardsView
      initiativeId={initiativeId}
      typeDistribution={typeDistribution}
      topEmitters={topEmitters}
      assetTotalCo2e={assetTotalCo2e}
      orgTotalCo2e={orgTotalCo2e}
      formatCompact={formatCompact}
      onAssetSelect={onAssetSelect}
    />
  );
};
